import React, { useState } from 'react';
import { Layout } from '@/components/Layout';
import { Lock, Unlock, Sparkles, Zap, Shield, Crown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useUnlock } from '@/contexts/UnlockContext';
import { useNavigate } from 'react-router-dom';

export default function UnlockPage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const { isUnlocked, unlock } = useUnlock();
  const navigate = useNavigate();

  const handleUnlock = () => {
    if (!code.trim()) {
      setError('请输入解锁码');
      return;
    }

    const success = unlock(code.trim());
    if (success) {
      setError('');
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } else {
      setError('解锁码错误，请重试');
      setCode('');
    }
  };

  const features = [
    { icon: <Zap className="h-5 w-5" />, title: '完整本地数据库', desc: '全部功能开启' },
    { icon: <Shield className="h-5 w-5" />, title: '无广告体验', desc: '纯净使用环境' },
    { icon: <Sparkles className="h-5 w-5" />, title: '自定义界面', desc: '个性化布局' },
    { icon: <Crown className="h-5 w-5" />, title: '高级数据统计', desc: '深度分析功能' },
  ];

  if (isUnlocked) {
    return (
      <Layout>
        <div className="container mx-auto px-4 md:px-6 py-12 max-w-2xl">
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-3xl p-12 text-center">
            <div className="bg-green-500 text-white p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <Unlock className="h-10 w-10" />
            </div>
            <h1 className="text-3xl font-extrabold mb-4">已解锁高级模式</h1>
            <p className="text-muted-foreground mb-8">
              您已成功解锁全部高级功能，尽情享受完整体验！
            </p>
            <Button size="lg" onClick={() => navigate('/')}>
              返回首页
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 md:px-6 py-12 max-w-4xl">
        <div className="flex flex-col items-center text-center mb-12">
          <div className="bg-primary/10 p-4 rounded-2xl mb-4">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight mb-4">高级解锁模式</h1>
          <p className="text-muted-foreground text-lg max-w-xl">
            输入解锁码，解锁全部高级功能，享受完整体验
          </p>
        </div>

        {/* Unlock Form */}
        <div className="max-w-md mx-auto mb-12">
          <div className="bg-card border rounded-3xl p-8 shadow-lg">
            <div className="space-y-6">
              <div>
                <label className="text-sm font-bold mb-2 block">解锁码</label>
                <Input
                  type="text"
                  placeholder="请输入解锁码"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setError('');
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && handleUnlock()}
                  className="text-center text-lg tracking-widest"
                />
                {error && (
                  <p className="text-red-500 text-sm mt-2">{error}</p>
                )}
              </div>

              <Button
                className="w-full h-12 text-lg font-bold"
                onClick={handleUnlock}
              >
                <Unlock className="mr-2 h-5 w-5" />
                立即解锁
              </Button>

              <div className="text-center text-xs text-muted-foreground">
                <p>解锁码获取方式：</p>
                <p className="mt-1">通过问卷获取邀请码 <span className="font-mono font-bold text-primary">02894</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {features.map((feature, idx) => (
            <div key={idx} className="bg-card border rounded-3xl p-6 hover:border-primary/50 transition-colors">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 text-primary p-3 rounded-2xl shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="font-bold mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="bg-card border rounded-3xl p-8 shadow-sm">
          <h2 className="text-2xl font-bold mb-6 text-center">功能对比</h2>
          <div className="space-y-4">
            {[
              { name: '全网通用导航', free: true, premium: true },
              { name: '基础查询功能', free: true, premium: true },
              { name: '公开内容浏览', free: true, premium: true },
              { name: '完整本地数据库', free: false, premium: true },
              { name: '无广告体验', free: false, premium: true },
              { name: '自定义界面布局', free: false, premium: true },
              { name: '高级数据统计', free: false, premium: true },
              { name: '隐私保护高级模式', free: false, premium: true },
              { name: '全部工具解锁', free: false, premium: true },
            ].map((item, idx) => (
              <div key={idx} className="grid grid-cols-3 gap-4 py-3 border-b last:border-0">
                <div className="col-span-1 font-medium">{item.name}</div>
                <div className="text-center">
                  {item.free ? (
                    <Check className="h-5 w-5 text-green-500 mx-auto" />
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </div>
                <div className="text-center">
                  {item.premium ? (
                    <Check className="h-5 w-5 text-primary mx-auto" />
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
            <div></div>
            <div className="text-center font-bold text-muted-foreground">普通模式</div>
            <div className="text-center font-bold text-primary">高级模式</div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
